export type PoolsPremiumWindow = "1d" | "7d" | "30d" | null;
export type PoolsColumnGroup = "identity" | "metrics";

export type PoolsColumnMeta = {
	id: string;
	label: string;
	period?: string;
	/** Shown in the Columns dialog. */
	picker: boolean;
	/** Hidden until the user opts in. */
	defaultHidden: boolean;
	pinned: boolean;
	premiumWindow: PoolsPremiumWindow;
	group: PoolsColumnGroup | null;
};

export const POOLS_COLUMNS: readonly PoolsColumnMeta[] = [
	{
		id: "favorite",
		label: "",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: null,
	},
	{
		id: "name",
		label: "Pool",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: null,
	},
	{
		id: "token1",
		label: "Token 1",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: "identity",
	},
	{
		id: "token2",
		label: "Token 2",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: "identity",
	},
	{
		id: "uniswap",
		label: "Ver.",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: "identity",
	},
	{
		id: "network",
		label: "Network",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: "identity",
	},
	{
		id: "feeTier",
		label: "Fee Tier",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: "identity",
	},
	{
		id: "tvl",
		label: "TVL",
		picker: true,
		defaultHidden: false,
		pinned: false,
		premiumWindow: null,
		group: "metrics",
	},
	{
		id: "apr1d",
		label: "APR",
		period: "(1D)",
		picker: true,
		defaultHidden: false,
		pinned: false,
		premiumWindow: "1d",
		group: "metrics",
	},
	{
		id: "volTvl",
		label: "Vol/TVL",
		period: "(1D)",
		picker: true,
		defaultHidden: false,
		pinned: false,
		premiumWindow: "1d",
		group: "metrics",
	},
	{
		id: "apr7d",
		label: "APR",
		period: "(7D)",
		picker: true,
		defaultHidden: false,
		pinned: false,
		premiumWindow: "7d",
		group: "metrics",
	},
	{
		id: "volTvl7d",
		label: "Vol/TVL",
		period: "(7D)",
		picker: true,
		defaultHidden: true,
		pinned: false,
		premiumWindow: "7d",
		group: "metrics",
	},
	{
		id: "apr30d",
		label: "APR",
		period: "(30D)",
		picker: true,
		defaultHidden: false,
		pinned: false,
		premiumWindow: "30d",
		group: "metrics",
	},
	{
		id: "volTvl30d",
		label: "Vol/TVL",
		period: "(30D)",
		picker: true,
		defaultHidden: true,
		pinned: false,
		premiumWindow: "30d",
		group: "metrics",
	},
	{
		id: "actions",
		label: "Analyze",
		picker: false,
		defaultHidden: false,
		pinned: true,
		premiumWindow: null,
		group: null,
	},
] as const;

const COLUMN_BY_ID = new Map(POOLS_COLUMNS.map((column) => [column.id, column]));

export const PICKER_COLUMN_IDS = POOLS_COLUMNS.filter((column) => column.picker).map(
	(column) => column.id,
);

export const DEFAULT_HIDDEN_COLUMN_IDS = POOLS_COLUMNS.filter((column) => column.defaultHidden).map(
	(column) => column.id,
);

export const VOL_TVL_COLUMN_IDS = ["volTvl", "volTvl7d", "volTvl30d"] as const;

/** Overlay starts at the first visible premium window column (7D/30D + Analyze). */
export const LOCKED_COLUMN_ORDER = ["apr7d", "volTvl7d", "apr30d", "volTvl30d", "actions"] as const;

const SORT_FALLBACK_ORDER = [
	"apr1d",
	"volTvl",
	"tvl",
	"feeTier",
	"token1",
	"token2",
	"uniswap",
	"network",
	"apr7d",
	"volTvl7d",
	"apr30d",
	"volTvl30d",
] as const;

export function pickerLabel(column: PoolsColumnMeta): string {
	return column.period ? `${column.label} ${column.period}` : column.label;
}

export function isPickerColumnId(id: string): boolean {
	return COLUMN_BY_ID.get(id)?.picker === true;
}

/** APR 7D/30D stay visible for guests/free so the paywall overlay has a target. */
export const PAYWALL_LOCKED_COLUMN_IDS = ["apr7d", "apr30d"] as const;

export function isPaywallLockedColumnId(id: string): boolean {
	return id === "apr7d" || id === "apr30d";
}

export function pickerColumns(canHidePaywallColumns: boolean): PoolsColumnMeta[] {
	return POOLS_COLUMNS.filter(
		(column) => column.picker && (canHidePaywallColumns || !isPaywallLockedColumnId(column.id)),
	);
}

export function sanitizeHiddenColumnIds(ids: unknown, canHidePaywallColumns = true): string[] {
	if (!Array.isArray(ids)) return [...DEFAULT_HIDDEN_COLUMN_IDS];
	const seen = new Set<string>();
	const next: string[] = [];
	for (const id of ids) {
		if (typeof id !== "string" || !isPickerColumnId(id) || seen.has(id)) continue;
		if (!canHidePaywallColumns && isPaywallLockedColumnId(id)) continue;
		seen.add(id);
		next.push(id);
	}
	return next;
}

export function hiddenIdsToVisibility(hiddenColumnIds: readonly string[]): Record<string, boolean> {
	const visibility: Record<string, boolean> = {};
	for (const id of hiddenColumnIds) {
		if (isPickerColumnId(id)) visibility[id] = false;
	}
	return visibility;
}

export function isColumnHidden(hiddenColumnIds: readonly string[], id: string): boolean {
	return hiddenColumnIds.includes(id);
}

export function withColumnVisibility(
	hiddenColumnIds: readonly string[],
	id: string,
	visible: boolean,
	canHidePaywallColumns = true,
): string[] {
	if (!isPickerColumnId(id)) return [...hiddenColumnIds];
	if (!canHidePaywallColumns && isPaywallLockedColumnId(id)) {
		return hiddenColumnIds.filter((hidden) => hidden !== id);
	}
	if (visible) return hiddenColumnIds.filter((hidden) => hidden !== id);
	if (hiddenColumnIds.includes(id)) return [...hiddenColumnIds];
	return [...hiddenColumnIds, id];
}

export function firstVisibleLockedColumnId(hiddenColumnIds: readonly string[]): string | null {
	const hidden = new Set(hiddenColumnIds);
	for (const id of LOCKED_COLUMN_ORDER) {
		if (!hidden.has(id)) return id;
	}
	return null;
}

/** Metrics the mobile card can show, in desktop column order. */
export const CARD_METRIC_COLUMN_IDS = [
	"apr1d",
	"volTvl",
	"apr7d",
	"volTvl7d",
	"apr30d",
	"volTvl30d",
] as const;

export function visibleCardMetricIds(hiddenColumnIds: readonly string[]): string[] {
	const hidden = new Set(hiddenColumnIds);
	return CARD_METRIC_COLUMN_IDS.filter((id) => !hidden.has(id));
}

export function fallbackSortColumnId(hiddenColumnIds: readonly string[]): string {
	const hidden = new Set(hiddenColumnIds);
	for (const id of SORT_FALLBACK_ORDER) {
		if (!hidden.has(id)) return id;
	}
	return "apr1d";
}
